import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, IconButton, Chip, LinearProgress,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Autocomplete, TextField, Paper, Tooltip, CircularProgress, Alert,
} from '@mui/material';
import {
    People as PeopleIcon,
    PersonAdd as PersonAddIcon,
    GroupAdd as GroupAddIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import ssoDataProvider from '../../ssoDataProvider';
import BulkEnrollmentDialog from '../Enrollments/BulkEnrollmentDialog';

interface CourseEnrollmentDialogProps {
    open: boolean;
    onClose: () => void;
    courseId: number;
    courseTitle: string;
}

interface Enrollment {
    id: number;
    userId: number;
    userName?: string;
    userEmail?: string;
    status: string;
    progressPercentage: number;
    enrolledAt: string;
    completedAt?: string;
}

interface User {
    id: number;
    firstName?: string;
    lastName?: string;
    email: string;
}

const getStatusColor = (status: string): 'success' | 'primary' | 'error' | 'warning' | 'default' => {
    switch (status) {
        case 'completed': return 'success';
        case 'in_progress': return 'primary';
        case 'failed': return 'error';
        case 'pending_approval': return 'warning';
        case 'rejected': return 'error';
        default: return 'default';
    }
};

const getStatusLabel = (status: string, translate: (key: string) => string): string => {
    return translate(`courseEnrollmentDialog.status.${status}`) || status;
};

const CourseEnrollmentDialog: React.FC<CourseEnrollmentDialogProps> = ({
    open, onClose, courseId, courseTitle
}) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const translate = useTranslate();

    const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState<User[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [enrolling, setEnrolling] = useState(false);
    const [showAddUser, setShowAddUser] = useState(false);
    const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

    const loadEnrollments = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('course_enrollments', {
                pagination: { page: 1, perPage: 500 },
                sort: { field: 'enrolledAt', order: 'DESC' },
                filter: { 'course.id': courseId, showAll: 'true' }
            });

            const rawEnrollments = data as Enrollment[];

            // Resolve user names from SSO
            const userIds = Array.from(new Set(rawEnrollments.map(e => e.userId).filter(Boolean)));
            let usersMap = new Map<number, User>();

            if (userIds.length > 0) {
                try {
                    const { data: usersData } = await ssoDataProvider.getMany('users', { ids: userIds });
                    usersData.forEach((u: any) => {
                        if (u && u.id) usersMap.set(Number(u.id), u);
                    });
                } catch {
                    // Fallback: user names won't be resolved
                }
            }

            // Enrich enrollments with user data
            const enriched = rawEnrollments.map(enrollment => {
                const user = usersMap.get(enrollment.userId);
                const fullName = user
                    ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                    : '';
                return {
                    ...enrollment,
                    userName: fullName || user?.email || `User ${enrollment.userId}`,
                    userEmail: user?.email || '',
                };
            });

            setEnrollments(enriched);

            // Cache users for the add-user autocomplete
            if (usersMap.size > 0 && users.length === 0) {
                setUsers(Array.from(usersMap.values()));
            }
        } catch {
            notify(translate('courseEnrollmentDialog.loadError'), { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [courseId, dataProvider, notify, translate]);

    const loadUsers = useCallback(async () => {
        if (users.length > 0) return;
        setUsersLoading(true);
        try {
            const { data } = await ssoDataProvider.getList('users', {
                pagination: { page: 1, perPage: 500 },
                sort: { field: 'lastName', order: 'ASC' },
                filter: {}
            });
            setUsers(data as User[]);
        } catch {
            // Silently fail
        } finally {
            setUsersLoading(false);
        }
    }, [users.length]);

    useEffect(() => {
        if (open) {
            loadEnrollments();
        }
    }, [open, loadEnrollments]);

    const handleEnroll = async () => {
        if (!selectedUser) return;
        setEnrolling(true);
        try {
            await dataProvider.create('course_enrollments', {
                data: {
                    userId: selectedUser.id,
                    course: `/api/courses/${courseId}`,
                    status: 'enrolled',
                    progressPercentage: 0
                }
            });
            notify(translate('courseEnrollmentDialog.enrollSuccess'), { type: 'success' });
            setSelectedUser(null);
            setShowAddUser(false);
            loadEnrollments();
        } catch (error: any) {
            const detail = error?.body?.detail || error?.body?.['hydra:description'] || error?.message || '';
            if (detail.includes('already enrolled') || detail.includes('Already enrolled')) {
                notify(translate('courseEnrollmentDialog.alreadyEnrolled'), { type: 'warning' });
            } else {
                notify(detail || translate('courseEnrollmentDialog.enrollError'), { type: 'error' });
            }
        } finally {
            setEnrolling(false);
        }
    };

    const handleDelete = async (enrollmentId: number) => {
        if (!window.confirm(translate('courseEnrollmentDialog.confirmRemove'))) return;
        try {
            await dataProvider.delete('course_enrollments', {
                id: enrollmentId,
                previousData: {} as any
            });
            notify(translate('courseEnrollmentDialog.removeSuccess'), { type: 'success' });
            loadEnrollments();
        } catch {
            notify(translate('courseEnrollmentDialog.removeError'), { type: 'error' });
        }
    };

    const handleShowAddUser = () => {
        loadUsers();
        setShowAddUser(true);
    };

    const handleBulkSuccess = () => {
        setBulkDialogOpen(false);
        loadEnrollments();
    };

    const enrolledUserIds = new Set(enrollments.map(e => e.userId));
    const availableUsers = users.filter(u => !enrolledUserIds.has(u.id));

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon />
                        <span>{translate('courseEnrollmentDialog.title')}</span>
                        <Chip label={courseTitle} size="small" variant="outlined" />
                        <Chip label={enrollments.length} size="small" color="primary" />
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {/* Action Bar */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<PersonAddIcon />}
                            onClick={handleShowAddUser}
                            size="small"
                        >
                            {translate('courseEnrollmentDialog.addUser')}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<GroupAddIcon />}
                            onClick={() => setBulkDialogOpen(true)}
                            size="small"
                        >
                            {translate('courseEnrollmentDialog.bulkEnroll')}
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <IconButton onClick={loadEnrollments} size="small">
                            <RefreshIcon />
                        </IconButton>
                    </Box>

                    {/* Single User Enrollment */}
                    {showAddUser && (
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <Autocomplete
                                    options={availableUsers}
                                    value={selectedUser}
                                    onChange={(_, newValue) => setSelectedUser(newValue)}
                                    getOptionLabel={(option) => {
                                        const name = `${option.firstName || ''} ${option.lastName || ''}`.trim();
                                        return name ? `${name} (${option.email})` : option.email;
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={translate('courseEnrollmentDialog.selectUser')}
                                            size="small"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {usersLoading && <CircularProgress size={18} />}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                                    sx={{ flex: 1 }}
                                    size="small"
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleEnroll}
                                    disabled={!selectedUser || enrolling}
                                    size="small"
                                >
                                    {enrolling ? <CircularProgress size={20} /> : translate('courseEnrollmentDialog.enroll')}
                                </Button>
                                <Button
                                    onClick={() => { setShowAddUser(false); setSelectedUser(null); }}
                                    size="small"
                                >
                                    {translate('ra.action.cancel')}
                                </Button>
                            </Box>
                        </Paper>
                    )}

                    {/* Enrollments Table */}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : enrollments.length === 0 ? (
                        <Alert severity="info">
                            {translate('courseEnrollmentDialog.noEnrollments')}
                        </Alert>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{translate('courseEnrollmentDialog.table.name')}</TableCell>
                                        <TableCell>{translate('courseEnrollmentDialog.table.email')}</TableCell>
                                        <TableCell>{translate('courseEnrollmentDialog.table.status')}</TableCell>
                                        <TableCell>{translate('courseEnrollmentDialog.table.progress')}</TableCell>
                                        <TableCell>{translate('courseEnrollmentDialog.table.enrolledAt')}</TableCell>
                                        <TableCell width={48}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {enrollments.map((enrollment) => (
                                        <TableRow key={enrollment.id} hover>
                                            <TableCell>{enrollment.userName || '-'}</TableCell>
                                            <TableCell>{enrollment.userEmail || '-'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getStatusLabel(enrollment.status, translate)}
                                                    color={getStatusColor(enrollment.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={enrollment.progressPercentage || 0}
                                                        sx={{ flex: 1, height: 6, borderRadius: 3 }}
                                                    />
                                                    <Typography variant="caption" sx={{ minWidth: 35 }}>
                                                        {Math.round(enrollment.progressPercentage || 0)}%
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {enrollment.enrolledAt
                                                    ? new Date(enrollment.enrolledAt).toLocaleDateString('de-DE')
                                                    : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={translate('courseEnrollmentDialog.remove')}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDelete(enrollment.id)}
                                                        color="error"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose}>
                        {translate('ra.action.close')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Enrollment Dialog */}
            <BulkEnrollmentDialog
                open={bulkDialogOpen}
                onClose={() => setBulkDialogOpen(false)}
                courses={[{ id: courseId, title: courseTitle }]}
                initialCourseId={courseId}
                onSuccess={handleBulkSuccess}
            />
        </>
    );
};

export default CourseEnrollmentDialog;
